import Vue from 'vue'
import apiClient from '@/services/axios'
import { notification } from 'ant-design-vue'

// import router and store, we need those to re-route and dispatch action on API error
import router from '@/router'

const ApiService = {
  query(resource, params) {
    return apiClient
      .get(resource + '?' + new URLSearchParams(params))
      .catch((error) => {
        throw new Error(`[BELEN] ApiService ${error}`)
      })
  },

  get(resource, slug = '') {
    if (slug !== '') {
      slug = `/${slug}`
    }
    return apiClient.get(`${resource}${slug}`).catch((error) => {
      throw new Error(`[BELEN] ApiService ${error}`)
    })
  },

  post(resource, params) {
    return apiClient.post(`${resource}`, params)
  },

  update(resource, slug, params) {
    if (slug !== '') {
      slug = `/${slug}`
    }
    return apiClient.put(`${resource}${slug}`, params)
  },

  put(resource, params) {
    return apiClient.put(`${resource}`, params)
  },

  delete(resource, data = {}) {
    return apiClient.delete(resource, data).catch((error) => {
      throw new Error(`[BELEN] ApiService ${error}`)
    })
  },

  /**
   * Download a file from an API path with auth (Bearer). Uses blob + object URL to trigger download.
   * @param {string} path - API path relative to baseURL (e.g. 'excel-report/2024', 'zip-file/uuid')
   * @param {string} defaultFilename - Fallback filename if Content-Disposition is missing
   */
  downloadFile(path, defaultFilename = 'download') {
    return apiClient.get(path, { responseType: 'blob' }).then((response) => {
      const blob = response.data
      let filename = defaultFilename
      const disposition = response.headers['content-disposition']
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename[*]?=['"]?(?:UTF-8'')?([^;\n"']+)['"]?/i)
        if (match) filename = decodeURIComponent(match[1].trim())
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    })
  },
}

export const UserService = {
  get(id = '') {
    return ApiService.get('user', id)
  },
  update(id, params) {
    return ApiService.update('user', id, params)
  },
  save(params) {
    return ApiService.post('user', params)
  },
  delete(id) {
    return ApiService.delete(`user/${id}`)
  },
  resetPassword(id) {
    return ApiService.post(`user/${id}/reset-password`)
  },
  resendActivation(id) {
    return ApiService.post(`user/${id}/resend-activation`)
  },
}

export const AdminUserService = {
  getAll() {
    return ApiService.get('admin-users')
  },
  create(params) {
    return ApiService.post('admin-users', params)
  },
  deactivate(id) {
    return ApiService.post(`admin-users/${id}/deactivate`)
  },
  reactivate(id) {
    return ApiService.post(`admin-users/${id}/reactivate`)
  },
}

export const ParticipantService = {
  get(id = '') {
    return ApiService.get('participant', id)
  },
  update(id, params) {
    return ApiService.update('participant', id, params)
  },
  save(params) {
    return ApiService.post('participant', params)
  },
  delete(id) {
    return ApiService.delete(`participant/${id}`)
  },
}

export const EpexService = {
  getMembers() {
    return ApiService.get('epex/members')
  },
  createMember(epexName, eicCode) {
    return ApiService.post('epex/members', { epexName, eicCode })
  },
  deleteMember(id) {
    return ApiService.delete(`epex/members/${id}`)
  },
}

export const XmlService = {
  getAuctionStatus() {
    return ApiService.get('xml/get_auction_status')
  },
  getHoursForDate(date) {
    return ApiService.get('xml/xml-bid-ask-times/' + date)
  },
  getBidAskCurves(params) {
    return ApiService.post('xml/xml-bid-ask-curves', params)
  },
  getDayAheadResult(params) {
    return ApiService.post('xml/xml-day-ahead-result', params)
  },
  getDayAheadResultTable(params) {
    var date = params.date
    return ApiService.get(`xml/xml-day-ahead-result/${date}`)
    //return ApiService.query('xml/xml-day-ahead-result', params)
  },
  getMarketResultBlockForTable(params) {
    return ApiService.query('xml/xml-market-result-blocks', params)
  },
  getDownloadedFiles() {
    return ApiService.get(`xml/downloaded_files`)
  },
  getZip(path) {
    return ApiService.get(`xml/create_zip/${path}`)
  },
  sendEmail(recordId, type) {
    return ApiService.post(`xml/send_xml`, { id: recordId, type })
  },
  sync() {
    return ApiService.post(`xml/sync`)
  },
}

export const AuctionService = {
  get(id = '') {
    return ApiService.get('auction', id)
  },
  list(params = {}) {
    return ApiService.query('auction', params)
  },
  getAuctionPdf(auctionId, participantId) {
    return ApiService.get(`auction/${auctionId}/pdf/${participantId}`)
  },
  getAuctionCsv(auctionId, participantId) {
    return ApiService.get(`auction/${auctionId}/csv/${participantId}`)
  },
  update(id, params) {
    return ApiService.update('auction', id, params)
  },
  save(params) {
    return ApiService.post('auction', params)
  },
  delete(id) {
    return ApiService.delete(`auction/${id}`)
  },
  cancel(id) {
    return ApiService.post(`auction/${id}/cancel`)
  },
  finalize(id) {
    return ApiService.post(`auction/${id}/finalize`)
  },
  getActiveAuctions() {
    return ApiService.get('auction/active')
  },
  createOrder(auctionId, orderData) {
    return ApiService.post(`auction/${auctionId}/order`, orderData)
  },
  cancelOrder(auctionId, orderId) {
    return ApiService.delete(`auction/${auctionId}/order/${orderId}`)
  },
  getOrders(auctionId) {
    return ApiService.get(`auction/${auctionId}/orders`)
  },
  updateParticipants(auctionId, participantIds) {
    return ApiService.post(`auction/${auctionId}/participants`, {
      participantIds: participantIds,
    })
  },
  sendParticipantReport(auctionId, participantId) {
    return ApiService.post(
      `auction/${auctionId}/participants/${participantId}/par`
    )
  },
}

export const SettingsService = {
  get(id = '') {
    return ApiService.get('settings', id)
  },
  update(id, params) {
    return ApiService.update('settings', id, params)
  },
}

export const AuctionTypesService = {
  get(id = '') {
    return ApiService.get('auctiontype', id)
  },
  update(id, params) {
    return ApiService.update('auctiontype', id, params)
  },
  save(params) {
    return ApiService.post('auctiontype', params)
  },
  delete(id) {
    return ApiService.delete(`auctiontype/${id}`)
  },
  swapActive(id) {
    return ApiService.update('auctiontype/swapActive', id)
  },
}

export const ProvisioningService = {
  get(id = '') {
    return ApiService.get('provisioning', id)
  },
  update(id, params) {
    return ApiService.update('provisioning', id, params)
  },
}

export const ApiKeyService = {
  get(id = '') {
    return ApiService.get('api-keys', id)
  },
  getAll() {
    return ApiService.get('api-keys')
  },
  create(params) {
    return ApiService.post('api-keys', params)
  },
  update(id, params) {
    return ApiService.update('api-keys', id, params)
  },
  delete(id) {
    return ApiService.delete(`api-keys/${id}`)
  },
  deactivate(id) {
    return ApiService.post(`api-keys/${id}/deactivate`)
  },
  reactivate(id) {
    return ApiService.post(`api-keys/${id}/reactivate`)
  },
  generateToken(id) {
    return ApiService.post(`api-keys/${id}/generate-token`)
  },
}

export default ApiService
